// Access rules for TrailBivvy Pro bought through Stripe — the web counterpart of the iOS EntitlementResolver
// (Packages/TrailBivvyKit/Sources/Store/EntitlementResolver.swift, iOS DECISION.md S3/S13). Used by the Worker to set a
// token's `exp` and by the app to decide access offline, so both always agree.
//
// - period running (trial or paid) → access until the period end;
// - cancelled (not renewing) → access ends at the period end;
// - period over, set to renew, renewal not confirmed on this device yet (no signal on the trail) → access for up to
//   14 days after the period end (`renewalUnconfirmed`), like iOS `offlineRenewalAllowance`;
// - payment problem (Stripe `past_due`) after a paid period → access during a 16-day billing grace period from the
//   failed renewal (the length iOS recommends for Apple's Billing Grace Period); a free trial whose first charge
//   fails is never extended (iOS S13); afterwards locked with status `billingRetry`;
// - `unpaid`, `canceled`, `incomplete*`, `paused` → no access.

import type { EntitlementClaims, StripeSubscriptionStatus } from './token';

export const DAY_SECONDS = 86_400;
/** iOS EntitlementResolver.offlineRenewalAllowance. */
export const offlineRenewalAllowanceSeconds = 14 * DAY_SECONDS;
/** Billing grace period after a failed renewal of a paid period. */
export const billingGraceSeconds = 16 * DAY_SECONDS;

export type SubscriptionStatus =
  | 'active'
  | 'gracePeriod'
  | 'renewalUnconfirmed'
  | 'billingRetry'
  | 'expired'
  | 'revoked';

export interface SubscriptionFacts {
  status: StripeSubscriptionStatus;
  /** Current period start/end, epoch seconds. */
  periodStart: number;
  periodEnd: number;
  /** The current period is the free trial. */
  isFreeTrial: boolean;
  /** The past_due period follows a trial (the first charge failed). */
  failedAfterTrial: boolean;
  willAutoRenew: boolean;
}

export interface Access {
  /** Last instant with access, epoch seconds. */
  until: number;
  status: SubscriptionStatus;
}

/** Access at `now` (epoch seconds) for one subscription, or undefined without access. */
export function accessFor(facts: SubscriptionFacts, now: number): Access | undefined {
  switch (facts.status) {
    case 'trialing':
    case 'active': {
      if (facts.periodEnd > now) return { until: accessEnd(facts), status: 'active' };
      if (facts.willAutoRenew && facts.periodEnd + offlineRenewalAllowanceSeconds > now) {
        return { until: facts.periodEnd + offlineRenewalAllowanceSeconds, status: 'renewalUnconfirmed' };
      }
      return undefined;
    }
    case 'past_due': {
      if (facts.failedAfterTrial) return undefined;
      const graceEnd = facts.periodStart + billingGraceSeconds;
      return graceEnd > now ? { until: graceEnd, status: 'gracePeriod' } : undefined;
    }
    default:
      return undefined;
  }
}

/**
 * The longest access a token for these facts may grant (its `exp`): the period end, plus the offline renewal
 * allowance when the subscription is set to renew, or the grace end for a payment problem.
 */
export function accessEnd(facts: SubscriptionFacts): number {
  switch (facts.status) {
    case 'trialing':
    case 'active':
      return facts.willAutoRenew ? facts.periodEnd + offlineRenewalAllowanceSeconds : facts.periodEnd;
    case 'past_due':
      return facts.failedAfterTrial ? facts.periodStart : facts.periodStart + billingGraceSeconds;
    default:
      return Math.min(facts.periodEnd, facts.periodStart);
  }
}

/** Status shown to the hiker when there is no access (paywall and More › TrailBivvy Pro). */
export function lockedStatus(facts: SubscriptionFacts): SubscriptionStatus {
  if (facts.status === 'past_due' || facts.status === 'unpaid') return 'billingRetry';
  return 'expired';
}

export function factsFromClaims(claims: EntitlementClaims): SubscriptionFacts {
  return {
    status: claims.st,
    periodStart: claims.ps,
    periodEnd: claims.pe,
    isFreeTrial: claims.tr,
    failedAfterTrial: claims.ft,
    willAutoRenew: claims.wr,
  };
}

/**
 * Access from a verified token at `now`: the token's own rules, never beyond its `exp` (a client clock moved forward
 * cannot extend access past what the Worker granted; moved backward it cannot pass the period start either).
 */
export function accessFromClaims(claims: EntitlementClaims, now: number): Access | undefined {
  if (now > claims.exp) return undefined;
  const access = accessFor(factsFromClaims(claims), now);
  if (!access) return undefined;
  return { until: Math.min(access.until, claims.exp), status: access.status };
}
